import { forwardRef } from "react";
import { cn } from "@/utils";
import {
  inputInnerPadding,
  inputShell,
  type InputShellVariantProps,
} from "../input-shell";

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  success?: boolean;
  error?: boolean;
  hint?: string;
  inputSize?: NonNullable<InputShellVariantProps["size"]>;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = "text",
      className = "",
      disabled = false,
      success = false,
      error = false,
      hint,
      inputSize = "lg",
      ...props
    },
    ref
  ) => {
    return (
      <div className="relative">
        <div
          className={cn(
            inputShell({
              size: inputSize,
              invalid: error,
            }),
            success &&
              "border-[var(--success)] focus-within:border-[var(--success)]",
            className
          )}
        >
          <input
            ref={ref}
            type={type}
            disabled={disabled}
            aria-invalid={error || props["aria-invalid"] || undefined}
            className={cn(
              "min-w-0 appearance-none text-sm disabled:cursor-not-allowed",
              inputInnerPadding({
                hasPrefix: false,
                hasSuffix: false,
                size: inputSize,
              })
            )}
            {...props}
          />
        </div>

        {hint && (
          <p
            className={`mt-1.5 text-xs ${
              error
                ? "text-error-500"
                : success
                ? "text-success-500"
                : "text-gray-500"
            }`}
          >
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
export { Input };
